import { Box, Stack, Typography } from '@mui/material';
import { first } from 'lodash';
import { useCallback, useMemo } from 'react';

import { EditButton } from '@/components/form/baseInputs/Button/EditButton';
import { DragAndDropList } from '@/components/lists/DragAndDropList/DragAndDropList';
import { TableCell, TableCellContext } from '@/components/lists/TableCell';
import {
  FeedbackMessages,
  useFeedback,
} from '@/components/notifications/Feedback/useFeedback';
import { useDebouncedFn } from '@/hooks/useDebouncedFn';
import { useModalState } from '@/hooks/useModalState';
import { useReportError } from '@/hooks/useReportError';
import {
  BehaviorAuthorizationType,
  useHasBehaviorAuthorization,
} from '@/modules/authentication/hooks/useHasBehaviorAuthorization';
import { SortableListItem } from '@/modules/estateTaxScenarios/forms/PaymentMethodForm/useDraggablePayoutOrderItems';
import {
  EstateWaterfall_ProposalFragment,
  EstateWaterfallDocument,
} from '@/modules/estateWaterfall/graphql/EstateWaterfall.generated';
import { ClientProfileSummaryPanel_ClientProfileFragment } from '@/modules/summaryPanels/ClientProfileSummaryPanel/graphql/ClientProfileSummaryPanel.generated';
import { OrganizationSummaryPanel_OrganizationFragment } from '@/modules/summaryPanels/OrganizationSummaryPanel/graphql/OrganizationSummaryPanel.generated';
import { COLORS } from '@/styles/tokens/colors';
import {
  formatCurrencyNoDecimals,
  formatCurrencyNoDecimalsAccounting,
} from '@/utils/formatting/currency';
import { getTransferDetails } from '@/utils/hypotheticalTransfers/transferUtils';

import { EntityDetail_EntityFragment } from '../graphql/EntityDetailPage.generated';
import { HypotheticalTransferModal } from '../HypotheticalTransferModal/HypotheticalTransferModal';
import { TransferWithValue } from './hooks/useGetHypotheticalTransfers';
import { useCreateOrUpdateHypotheticalTransfersMutation } from './hypotheticalTransfers/graphql/CreateOrUpdateHypotheticalTransfers.generated';
import { TransferDirection } from './hypotheticalTransfers/HypotheticalTransferForm';
import { getUpdateHypotheticalTransfersOrderInput } from './hypotheticalTransfers/hypotheticalTransfers.utils';
import {
  getDescriptionFromTransfer,
  getNetHypotheticalTransfers,
  getTransferDisplayName,
} from './HypotheticalTransfersCard.utils';

export enum TransferTab {
  Outbound = 'outbound',
  Inbound = 'inbound',
}

interface TransferItemsProps {
  transfers: {
    inboundTransfers: TransferWithValue[];
    outboundTransfers: TransferWithValue[];
  };
  activeTab: TransferTab;
  transferReferenceObj?:
    | EntityDetail_EntityFragment
    | ClientProfileSummaryPanel_ClientProfileFragment
    | EstateWaterfall_ProposalFragment
    | OrganizationSummaryPanel_OrganizationFragment;
  waterfallId: string;
  householdId: string;
  entityId: string;
}

export function TransferItems({
  transfers: { inboundTransfers, outboundTransfers },
  activeTab,
  waterfallId,
  householdId,
  entityId,
}: TransferItemsProps) {
  const { showFeedback, createSuccessFeedback } = useFeedback();
  const { reportError } = useReportError();
  const canModifyHypotheticalTransfers = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_MODIFY_HYPOTHETICAL_TRANSFERS
  );

  const isOutbound = activeTab === TransferTab.Outbound;
  const transfers = isOutbound ? outboundTransfers : inboundTransfers;

  const [{ isModalOpen, data: transferId }, { openModal, closeModal }] =
    useModalState<string | null>();

  const items = useMemo(() => {
    return transfers
      .sort((a, b) => a.transfer.transferOrder - b.transfer.transferOrder)
      .map((transferWithValue) => {
        const { amount: transferValue, date: subheading } = getTransferDetails(
          transferWithValue.transfer
        );

        let heading = formatCurrencyNoDecimals(transferValue);

        if (isOutbound) {
          heading = formatCurrencyNoDecimalsAccounting(transferValue.times(-1));
        }

        let headingSx = undefined;

        if (activeTab === TransferTab.Outbound) {
          headingSx = { color: COLORS.MATH.NEGATIVE };
        }

        return {
          id: transferWithValue.transfer.id,
          heading: getTransferDisplayName(transferWithValue.transfer),
          description: getDescriptionFromTransfer(
            transferWithValue.transfer,
            activeTab
          ),
          context: {
            heading,
            headingSx,
            subheading,
            action: canModifyHypotheticalTransfers ? (
              <EditButton
                onClick={() => {
                  openModal(transferWithValue.transfer.id);
                }}
              />
            ) : undefined,
          },
        };
      });
  }, [
    activeTab,
    canModifyHypotheticalTransfers,
    isOutbound,
    openModal,
    transfers,
  ]);

  const [updateEstateWaterfall] =
    useCreateOrUpdateHypotheticalTransfersMutation({
      refetchQueries: [EstateWaterfallDocument],
      onCompleted: createSuccessFeedback('Hypothetical transfers updated'),
      onError: (error) => {
        showFeedback(FeedbackMessages.formSaveError);
        reportError('Error updating hypothetical transfers', error);
      },
    });

  const debouncedUpdateEstateWaterfall = useDebouncedFn(
    updateEstateWaterfall,
    500,
    {
      leading: false,
      trailing: true,
    }
  );

  const handleDrop = useCallback(
    (sortedList: SortableListItem[]) => {
      if (!sortedList.length) {
        return;
      }

      void debouncedUpdateEstateWaterfall({
        variables: {
          input: getUpdateHypotheticalTransfersOrderInput({
            waterfallId,
            transfers: sortedList,
          }),
        },
      });
    },
    [debouncedUpdateEstateWaterfall, waterfallId]
  );

  const firstItem = first(items);

  const { totalNormalizedTransfers: netTransferAmount } =
    getNetHypotheticalTransfers({
      inboundTransfers: isOutbound ? [] : transfers,
      outboundTransfers: isOutbound ? transfers : [],
      recipientLoans: [],
      sourceLoans: [],
      recipientGrats: [],
      sourceGrats: [],
      transferLog: [],
      inboundGratAnnuities: [],
      outboundGratAnnuities: [],
      outboundBeneficiaryPayments: [],
      inboundBeneficiaryPayments: [],
    });

  return (
    <>
      {isModalOpen && transferId && (
        <HypotheticalTransferModal
          transferId={transferId}
          waterfallId={waterfallId}
          householdId={householdId}
          isOpen={isModalOpen}
          onClose={closeModal}
          initiatingDirection={
            isOutbound ? TransferDirection.Outbound : TransferDirection.Inbound
          }
          initiatingId={entityId}
        />
      )}
      <Stack>
        <Stack p={1.5} direction="row" bgcolor={COLORS.GRAY[100]}>
          <Box>
            <Typography
              variant="title-xs"
              component="p"
              color={COLORS.TEXT.SECONDARY}
            >
              Transfers
            </Typography>
          </Box>
        </Stack>
        <Stack
          direction="column"
          sx={{
            borderBottom: `1px solid ${COLORS.GRAY[200]}`,
            '&:last-child': {
              borderBottom: 'none',
            },
          }}
        >
          {items.length === 1 && firstItem ? (
            <TableCell
              RightContent={
                firstItem.context && <TableCellContext {...firstItem.context} />
              }
              {...firstItem}
            />
          ) : (
            <DragAndDropList<SortableListItem>
              items={items}
              respondToLengthChanges={false}
              ItemInnerComponent={({ item, Draghandle }) => (
                <TableCell
                  RightContent={
                    item.context && <TableCellContext {...item.context} />
                  }
                  {...item}
                  Draghandle={Draghandle}
                />
              )}
              droppableId={`${activeTab}-transfers`}
              onDropped={handleDrop}
            />
          )}
          <Stack
            sx={{
              borderTop: `2px solid ${COLORS.NAVY[600]}`,
              px: 1.5,
              py: 2.5,
            }}
            direction="row"
            justifyContent="space-between"
          >
            <Typography variant="title-s" component="span">
              Transferred {isOutbound ? 'out' : 'in'}
            </Typography>
            <Typography
              variant="title-s"
              component="span"
              sx={{
                color: isOutbound ? COLORS.MATH.NEGATIVE : undefined,
              }}
            >
              {formatCurrencyNoDecimalsAccounting(
                netTransferAmount.times(isOutbound ? -1 : 1)
              )}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </>
  );
}
